/**
 * SimpleEdge (Canvas V2)
 *
 * Single edge type: white Bezier curve, hidden by default.
 * Visibility controlled by connected-node state.
 *
 * Visibility rules (priority order):
 * 1. Running (source or target status === 'running'): 90% opacity
 * 2. Selected (source or target node selected, or edge selected): 90% opacity
 * 3. Hovered (source or target node hovered via NodeHoverContext): 50% opacity
 * 4. Default: 0% opacity (invisible)
 *
 * Dead code removed: LabeledEdge, TaperedEdge, FloatingEdgeUtils,
 * canvasDragState, connectionGenerator (per spec 03).
 */

import { memo, useContext, useMemo } from 'react'
import { EdgeProps, getBezierPath, useStore } from 'reactflow'
import { NodeHoverContext } from './NodeHoverContext'

/** Read node execution status from the React Flow store. */
function selectNodeStatus(nodeId: string) {
  return (store: { nodeInternals: Map<string, Record<string, unknown>> }): string => {
    const n = store.nodeInternals.get(nodeId) as Record<string, unknown> | undefined
    if (!n) return 'idle'
    const data = n.data as Record<string, unknown> | undefined
    return (data?.status as string) ?? 'idle'
  }
}

/** Check if a node is selected. */
function selectNodeSelected(nodeId: string) {
  return (store: { nodeInternals: Map<string, Record<string, unknown>> }): boolean => {
    const n = store.nodeInternals.get(nodeId) as Record<string, unknown> | undefined
    return (n?.selected as boolean) ?? false
  }
}

export const SimpleEdge = memo(function SimpleEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
}: EdgeProps) {
  // Memoize selectors to prevent zustand re-subscriptions
  const sourceStatusSel = useMemo(() => selectNodeStatus(source), [source])
  const targetStatusSel = useMemo(() => selectNodeStatus(target), [target])
  const sourceSelectedSel = useMemo(() => selectNodeSelected(source), [source])
  const targetSelectedSel = useMemo(() => selectNodeSelected(target), [target])

  const sourceStatus = useStore(sourceStatusSel)
  const targetStatus = useStore(targetStatusSel)
  const sourceSelected = useStore(sourceSelectedSel)
  const targetSelected = useStore(targetSelectedSel)
  const { hoveredNodeId } = useContext(NodeHoverContext)

  const isActive = sourceStatus === 'running' || targetStatus === 'running'
  const isNodeSelected = sourceSelected || targetSelected
  const isEdgeSelected = selected === true
  const isNodeHovered = hoveredNodeId === source || hoveredNodeId === target

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  // Determine opacity (priority: running/selected > hovered > default)
  let opacity = 0
  if (isActive || isNodeSelected || isEdgeSelected) {
    opacity = 0.9
  } else if (isNodeHovered) {
    opacity = 0.5
  }

  return (
    <g className="react-flow__edge-interaction">
      {/* Invisible 20px-wide hit area for easier hover/click targeting */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        className="react-flow__edge-interaction"
      />
      {/* Visible white Bezier edge */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke="#ffffff"
        strokeWidth={1.5}
        opacity={opacity}
        className="react-flow__edge-path"
        style={{
          transition: 'opacity 0.2s ease-in-out',
        }}
      />
    </g>
  )
})

/**
 * Edge types registry for React Flow.
 * Only SimpleEdge exists (no LabeledEdge, no TaperedEdge).
 */
export const edgeTypes = {
  default: SimpleEdge,
}
